import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import { getChatHistory, getNewMessages, sendMessage } from "../services/chatService";
import "../css/SupportScreen.css";
import sendIcon from "../assets/icons/send.png";
import backIcon from "../assets/icons/back.png";
import { toast } from "react-toastify";

const ADMIN_ORIGIN = "https://admin.thedpboss.com";

const getImageCandidates = (value) => {
  if (!value || value instanceof File) return [];
  const rawPath = String(value).trim();
  if (rawPath.startsWith("blob:") || rawPath.startsWith("data:")) return [rawPath];

  const withoutOrigin = rawPath.startsWith(ADMIN_ORIGIN)
    ? rawPath.slice(ADMIN_ORIGIN.length)
    : rawPath;
  const cleanPath = withoutOrigin
    .replace(/^https?:\/\/admin\.thedpboss\.com/i, "")
    .replace(/^public\//, "")
    .replace(/^\/public\//, "/")
    .replace(/^storage\/app\/public\//, "storage/")
    .replace(/^\/storage\/app\/public\//, "/storage/");

  if (/^https?:\/\//i.test(rawPath)) {
    return Array.from(new Set([
      rawPath,
      `${ADMIN_ORIGIN}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`,
    ]));
  }

  const pathWithSlash = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  const withoutStoragePrefix = pathWithSlash.replace(/^\/storage\//, "/");

  return Array.from(new Set([
    `${ADMIN_ORIGIN}${pathWithSlash}`,
    `${ADMIN_ORIGIN}/storage${withoutStoragePrefix}`,
    `${ADMIN_ORIGIN}/public${pathWithSlash}`,
  ]));
};

function ChatImage({ sources, onOpen }) {
  const [sourceIndex, setSourceIndex] = useState(0);
  const sourceList = sources || [];
  const currentSource = sourceList[sourceIndex];

  if (!currentSource) return null;

  return (
    <img
      src={currentSource}
      alt=""
      onClick={() => onOpen(currentSource)}
      onError={() => setSourceIndex((prev) => prev + 1)}
    />
  );
}

export default function SupportScreen() {
  const navigate = useNavigate();
  const { user } = useContext(AppContext);
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const lastMessageIdRef = useRef(0);

  const userId = useMemo(() => {
    const localId = Number.parseInt(localStorage.getItem("registerId"), 10);
    if (Number.isInteger(localId)) return localId;

    const contextId = Number.parseInt(
      user?.userId ?? user?.registerId ?? user?.id,
      10
    );
    return Number.isInteger(contextId) ? contextId : null;
  }, [user]);

  const normalizeMessages = (items) => {
    if (!Array.isArray(items)) return [];

    return items
      .map((msg, index) => {
        const senderId = Number.parseInt(
          msg?.sender_id ?? msg?.senderId ?? msg?.user_id ?? msg?.userId,
          10
        );
        const receiverId = Number.parseInt(
          msg?.receiver_id ?? msg?.receiverId,
          10
        );
        const messageText =
          msg?.message ?? msg?.text ?? msg?.msg ?? msg?.content ?? "";
        const rawImageUrl =
          msg?.full_image_url ??
          msg?.fullImageUrl ??
          msg?.image ??
          msg?.image_url ??
          msg?.imageUrl ??
          msg?.file ??
          msg?.file_url ??
          msg?.attachment ??
          msg?.attachment_url ??
          "";
        const imageCandidates = getImageCandidates(rawImageUrl);

        const createdAt = msg?.created_at ?? msg?.createdAt ?? msg?.time ?? "";
        const resolvedId =
          msg?.id ??
          msg?.message_id ??
          msg?.messageId ??
          `${createdAt || Date.now()}-${index}`;

        return {
          ...msg,
          id: resolvedId,
          sender_id: Number.isInteger(senderId) ? senderId : null,
          receiver_id: Number.isInteger(receiverId) ? receiverId : null,
          message: messageText,
          image: imageCandidates[0] || "",
          imageCandidates,
          created_at: createdAt,
        };
      })
      .filter((msg) => msg.message || msg.image)
      .sort((a, b) => {
        const aTime = Date.parse(a.created_at || "") || 0;
        const bTime = Date.parse(b.created_at || "") || 0;
        if (aTime !== bTime) return aTime - bTime;
        return String(a.id).localeCompare(String(b.id));
      });
  };

  const mergeMessages = (prev, incoming) => {
    const map = new Map();
    [...prev, ...incoming].forEach((msg) => {
      const key = String(msg.id);
      map.set(key, msg);
    });
    return Array.from(map.values());
  };

  // Scroll to bottom helper
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch initial history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await getChatHistory(userId);
      if (res.status && res.info) {
        setChat(normalizeMessages(res.info));
      }
      setLoading(false);
    };

    fetchHistory();
  }, [userId]);

  // Handle Polling
  useEffect(() => {
    if (!userId) return;

    const startPolling = () => {
      pollingRef.current = setInterval(async () => {
        const res = await getNewMessages(userId, lastMessageIdRef.current);
        if (res.status && res.info && res.info.length > 0) {
          const normalized = normalizeMessages(res.info);
          setChat((prev) => mergeMessages(prev, normalized));
        }
      }, 5000); // 5 seconds
    };

    startPolling();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [userId]);

  useEffect(() => {
    // Only track real numeric IDs from the server for polling
    const serverMessages = chat.filter(m => typeof m.id === 'number' || !String(m.id).startsWith('local-'));
    if (serverMessages.length > 0) {
      lastMessageIdRef.current = serverMessages[serverMessages.length - 1].id;
    }
  }, [chat]);

  // Auto scroll when chat updates
  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  const handleSend = async () => {
    if (!message.trim() && !selectedImage) return;
    if (!userId) {
      toast.error("Please login to chat");
      return;
    }

    setSending(true);
    const payload = {
      sender_id: userId,
      receiver_id: 1,
      message: message.trim(),
      image: selectedImage,
      type: selectedImage ? "image" : "text"
    };

    const res = await sendMessage(payload);
    const isSuccess = res && res.status === true;

    if (isSuccess) {
      const localEcho = {
        id: `local-${Date.now()}`,
        sender_id: userId,
        receiver_id: 1,
        message: message.trim(),
        image: selectedImage ? previewUrl || "" : "",
        imageCandidates: selectedImage ? [previewUrl || ""] : [],
        created_at: new Date().toISOString(),
      };

      const serverMessage =
        res.data ||
        (Array.isArray(res.info) ? res.info[0] : res.info) ||
        (Array.isArray(res.messages) ? res.messages[0] : null) ||
        (Array.isArray(res.message) ? res.message[0] : null);

      if (serverMessage) {
        const normalizedServerMessages = normalizeMessages([serverMessage]);
        const serverHasImage = normalizedServerMessages.some((item) => item.image);
        const nextMessages =
          selectedImage && !serverHasImage
            ? [localEcho, ...normalizedServerMessages]
            : normalizedServerMessages;

        setChat((prev) => mergeMessages(prev, nextMessages));
      } else {
        // Fallback to local echo if status is success but no message returned
        setChat((prev) => mergeMessages(prev, [localEcho]));
      }

      setMessage("");
      setSelectedImage(null);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } else {
      toast.error(res?.msg || res?.message || "Failed to send message");
    }
    setSending(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return "";
    }
  };

  return (
    <div className="support-screen-wrapper">
      {/* Header */}
      <div className="support-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <img src={backIcon} alt="Back" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
        </button>
        <h1>Chat Support</h1>
      </div>

      {/* Chat Area */}
      <div className="chat-messages">
        {loading ? (
          <div className="chat-loading">
            <div className="spinner"></div>
            <span>Loading conversation...</span>
          </div>
        ) : chat.length === 0 ? (
          <div className="chat-empty-state">
            <div className="empty-icon">💬</div>
            <h3>No messages yet</h3>
            <p>Start a conversation with our support team. We're here to help you!</p>
          </div>
        ) : (
          chat.map((msg) => (
            <div 
              key={msg.id} 
              className={`chat-bubble ${(Number.parseInt(msg.sender_id, 10) || 0) === userId ? "sent" : "received"}`}
            >
              {msg.image && (
                <ChatImage
                  sources={msg.imageCandidates?.length ? msg.imageCandidates : [msg.image]}
                  onOpen={setFullscreenImage}
                />
              )}
              {msg.message && <div>{msg.message}</div>}
              <span className="message-time">{formatTime(msg.created_at)}</span>
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {fullscreenImage && (
        <div className="image-preview-overlay" onClick={() => setFullscreenImage(null)}>
          <div className="preview-header">
            <button className="close-preview" onClick={() => setFullscreenImage(null)}>&times;</button>
            <span style={{ marginLeft: '16px' }}>Preview Image</span>
          </div>
          <div className="preview-content">
            <img src={fullscreenImage} alt="Preview" />
          </div>
        </div>
      )}

      {/* Message Input */}
      {!previewUrl && (
        <div className="chat-input-wrapper">
          <div className="input-container">
            <button className="attach-btn" onClick={() => fileInputRef.current.click()}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z" />
              </svg>
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept="image/*" 
              onChange={handleImageSelect}
            />
            <input
              type="text"
              className="chat-input"
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
          </div>
          <button className="send-btn" onClick={handleSend} disabled={sending || (!message.trim() && !selectedImage)}>
            {sending ? "..." : <img src={sendIcon} alt="Send" />}
          </button>
        </div>
      )}

      {previewUrl && (
        <div className="selected-image-row">
          <div className="selected-image-card">
            <img src={previewUrl} alt="Selected" />
            <button className="close-preview" onClick={closePreview}>&times;</button>
          </div>
          <div className="input-container preview-input-container">
            <input
              type="text"
              className="chat-input"
              placeholder="Add a caption..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
          </div>
          <button className="send-btn" onClick={handleSend} disabled={sending}>
            {sending ? "..." : <img src={sendIcon} alt="Send" />}
          </button>
        </div>
      )}
    </div>
  );
}
